import React, { useState, useCallback, useRef, useEffect } from 'react';
import { InoText } from '../InoText/InoText';
import { useMappedKeydown } from '../../hooks';

const ArrowIcon = ({ rotated }: { rotated: boolean }) => {
  return (
    <svg
      fill="#fff"
      width={18}
      height={18}
      viewBox="0 0 16 16"
      xmlns="http://www.w3.org/2000/svg"
      className={`toggleIcon ${rotated ? 'rotate' : ''}`}
    >
      <path d="M4 6L8 10L12 6" stroke="#fff" strokeWidth="1.5" />
    </svg>
  );
};

const SelectedIcon = () => {
  return (
    <svg
      viewBox="0 0 1024 1024"
      xmlns="http://www.w3.org/2000/svg"
      fill="#fff"
      width={18}
      height={18}
      className="selectedIcon"
    >
      <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
      <g
        id="SVGRepo_tracerCarrier"
        strokeLinecap="round"
        strokeLinejoin="round"
      ></g>
      <g id="SVGRepo_iconCarrier">
        <path
          fill="#fff"
          d="M77.248 415.04a64 64 0 0 1 90.496 0l226.304 226.304L846.528 188.8a64 64 0 1 1 90.56 90.496l-543.04 543.04-316.8-316.8a64 64 0 0 1 0-90.496z"
        ></path>
      </g>
    </svg>
  );
};

export interface InoSelectOption {
  label: string;
  value: string | number;
  element?: React.ReactNode;
}

export interface InoSelectProps {
  options: InoSelectOption[];
  value?: string | number;
  onChange?: (value: string | number) => void;
  placeholder?: string;
  isActive?: boolean;
  disabled?: boolean;
  classNames?: string;
  showArrow?: boolean;
  showSelectedIcon?: boolean;
  onActiveChange?: (index: number) => void;
  onOpenChange?: (open: boolean) => void;
  onUp?: () => void;
  onDown?: () => void;
  onLeft?: () => void;
  onRight?: () => void;
  onBack?: () => void;
}

export const InoSelect: React.FC<InoSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  isActive = false,
  disabled = false,
  classNames = '',
  showArrow = true,
  showSelectedIcon = true,
  onActiveChange,
  onOpenChange,
  onUp,
  onDown,
  onLeft,
  onRight,
  onBack,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(
    () => options.findIndex((option) => option.value === value) || 0
  );
  const [selectedOptionLabel, setSelectedOptionLabel] = useState(
    options[selectedIndex].label
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedItem, setSelectedItem] = useState(
    options[selectedIndex].value
  );
  const optionsRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (isOpen && optionsRefs.current[activeIndex]) {
      optionsRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex, isOpen]);

  const handleKeyDown = useCallback(
    (key: string) => {
      if (isOpen) {
        if (key === 'ok') {
          onOpenChange?.(false);
          setSelectedItem(options[activeIndex].value);
          setSelectedOptionLabel(options[activeIndex].label);
          onChange?.(options[activeIndex].value);
          setIsOpen(false);
        }

        if (key === 'up') {
          if (activeIndex > 0) {
            setActiveIndex((prev) => prev - 1);
            onActiveChange?.(activeIndex);
          } else {
            setIsOpen(false);
            onOpenChange?.(false);
          }
        }
        if (key === 'down' && activeIndex < options.length - 1) {
          setActiveIndex((prev) => prev + 1);
          onActiveChange?.(activeIndex);
        }
        if (key === 'back') {
          setIsOpen(false);
          setActiveIndex(0);
          onOpenChange?.(false);
        }
      } else {
        if (key === 'ok') {
          setIsOpen(true);
          onOpenChange?.(true);
        } else if (key === 'up') {
          onUp?.();
        } else if (key === 'down') {
          onDown?.();
        } else if (key === 'left') {
          onLeft?.();
        } else if (key === 'right') {
          onRight?.();
        } else if (key === 'back') {
          onBack?.();
        }
      }
    },
    [
      isOpen,
      options,
      onChange,
      onActiveChange,
      onOpenChange,
      onUp,
      onDown,
      onLeft,
      onRight,
      onBack,
      activeIndex,
    ]
  );

  useMappedKeydown({
    isActive: isActive && !disabled,
    onDown: () => handleKeyDown('down'),
    onUp: () => handleKeyDown('up'),
    onBack: () => handleKeyDown('back'),
    onLeft: () => onLeft?.(),
    onRight: () => onRight?.(),
    onOk: () => {
      handleKeyDown('ok');
    },
  });

  return (
    <div
      className={`select ${isActive ? 'active' : ''} ${
        disabled ? 'disabled' : ''
      } ${classNames}`}
    >
      <div
        className={`selectTrigger ${isOpen ? 'open' : ''}`}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
      >
        <InoText variant="body">{selectedOptionLabel || placeholder}</InoText>
        {showArrow && <ArrowIcon rotated={isOpen} />}
      </div>

      {isOpen && (
        <div className="options">
          {options.map((option, index) => (
            <div
              key={option.value}
              ref={(el) => {
                optionsRefs.current[index] = el;
              }}
              className={`option ${activeIndex === index ? 'active' : ''}`}
              onClick={() => {
                if (!disabled) {
                  setSelectedIndex(index);
                  onChange?.(option.value);
                  setIsOpen(false);
                }
              }}
            >
              {showSelectedIcon && selectedItem === option.value && (
                <SelectedIcon />
              )}
              <InoText variant="body" classNames="label">
                {option.label}
              </InoText>
              {option.element ? option.element : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
